import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TripDatesService } from '@lx/quote-tool/shared/services';
import { DurationTypes, TripDates } from '@lx/quote-tool/shared/types';
import { ComponentStore, OnStoreInit, tapResponse } from '@ngrx/component-store';
import { combineLatest, filter, iif, Observable, of, skip, take } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { CourseDetails } from '@lx/quote-tool/lite/shared/types';
import { CourseTreeNode, GroupedAyaCourse } from '../../types';
import { QuoteToolLiteStore } from '../quote-tool-lite.store';
import { CourseState } from './course.state';
import { AyaCourseService, CourseService } from '../../services';
import { DEFAULT_WEEKS_RANGE, nonNullish } from '@lx/shared/utils';
import { SelectItem } from 'primeng/api';
import { Flows, SchoolLocation } from '@lx/quote-tool/lite/shared/types';

@Injectable()
export class CourseStore extends ComponentStore<CourseState> implements OnStoreInit {
  private readonly tripDatesService = inject(TripDatesService);
  private readonly courseService = inject(CourseService);
  private readonly quoteToolLiteStore = inject(QuoteToolLiteStore);
  private readonly ayaCourseService = inject(AyaCourseService);

  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    super({});
  }

  ngrxOnStoreInit(): void {
    combineLatest([this.quoteToolLiteStore.activeFlow$, this.quoteToolLiteStore.courseDetails$])
      .pipe(take(1))
      .subscribe(
        ([
          activeFlow,
          {
            selectedLanguage,
            selectedSchoolLocation,
            selectedCourse,
            selectedDuration,
            selectedAyaDuration,
            selectedStartDate,
            selectedEndDate,
            selectedTimezone,
            selectedClassTime,
          },
        ]) => {
          this.updateState({
            selectedLanguage,
            selectedSchoolLocation,
            selectedCourse,
            selectedDuration,
            selectedAyaDuration,
            selectedStartDate,
            selectedEndDate,
            selectedTimezone,
            selectedClassTime,
          });

          // Reset selected dates when the product package changes
          this.resetSelectedDatesWhenPackageChanges();

          if (activeFlow !== Flows.Junior && activeFlow !== Flows.Aya) {
            this.updateSelectedStartDate$(this.selectedStartDate$);
          }

          if (activeFlow === Flows.Aya) {
            this.updateSelectedAyaStartDate$(this.selectedStartDate$);
          }
        },
      );

    // Make sure that all time selectedLanguage is set on qtLite store, it is updated here too
    this.quoteToolLiteStore.selectedLanguage$.pipe(skip(1), takeUntilDestroyed()).subscribe((selectedLanguage) => {
      if (selectedLanguage) {
        this.updateState({ selectedLanguage });
      }
    });

    // Centralised place to load courses tree when school location changes
    this.prepareAdultCoursesTreeFillingOnSchoolLocationChange();

    // Centralised place to clear selected course if it is not in the list of available courses
    this.prepareAdultSelectedCourseResetOnUnavailable();

    // Centralised place to reset selected duration when it becomes incompatible with available durations
    this.prepareSelectedDurationResetOnUnavailable();

    // Centralised place to load aya courses when selected language changes
    this.prepareAyaCoursesFillingOnLanguageChange();

    // Centralised place to clear selected course if it is not in the list of available courses
    this.prepareAyaSelectedCourseResetOnUnavailable();
  }

  // Selectors
  readonly selectedLanguage$ = this.select(({ selectedLanguage }) => selectedLanguage);
  readonly selectedSchoolLocation$ = this.select(({ selectedSchoolLocation }) => selectedSchoolLocation, {
    equal: (prev, curr) => prev?.schoolCode === curr?.schoolCode,
  });
  readonly selectedCourse$ = this.select((state) => state.selectedCourse);
  readonly selectedDuration$ = this.select(({ selectedDuration }) => selectedDuration);
  readonly selectedAyaDuration$ = this.select(({ selectedAyaDuration }) => selectedAyaDuration);
  readonly selectedStartDate$ = this.select(({ selectedStartDate }) => selectedStartDate);
  readonly selectedDates$ = this.select(
    ({ selectedStartDate, selectedEndDate }) => ({
      selectedStartDate,
      selectedEndDate,
    }),
    { debounce: true },
  );

  readonly selectedTimezone$ = this.select(({ selectedTimezone }) => selectedTimezone);

  readonly isAdultValid$ = this.select(
    ({ selectedLanguage, selectedSchoolLocation, selectedCourse, selectedDuration, selectedStartDate }) =>
      !!selectedLanguage && !!selectedSchoolLocation && !!selectedCourse && !!selectedDuration && !!selectedStartDate,
    { debounce: true },
  );

  readonly isJuniorValid$ = this.select(
    ({ selectedLanguage, selectedSchoolLocation, selectedCourse, selectedStartDate, selectedEndDate }) =>
      !!selectedLanguage && !!selectedSchoolLocation && !!selectedCourse && !!selectedStartDate && !!selectedEndDate,
    { debounce: true },
  );

  readonly isAyaValid$ = this.select(
    ({ selectedLanguage, selectedSchoolLocation, selectedCourse, selectedAyaDuration, selectedStartDate }) =>
      !!selectedLanguage && !!selectedSchoolLocation && !!selectedCourse && !!selectedAyaDuration && !!selectedStartDate,
    { debounce: true },
  );

  readonly isOnlineValid$ = this.select(
    ({ selectedDuration, selectedStartDate, selectedTimezone, selectedCourse }) =>
      !!selectedDuration && !!selectedStartDate && !!selectedTimezone && !!selectedCourse,
    { debounce: true },
  );

  readonly selectedSchoolLocationAndLanguage$ = this.select(
    ({ selectedSchoolLocation, selectedLanguage }) => ({
      selectedSchoolLocation,
      selectedLanguage,
    }),
    {
      debounce: true,
      equal: (previous, current) => {
        return (
          previous.selectedSchoolLocation?.schoolCode === current.selectedSchoolLocation?.schoolCode && previous.selectedLanguage === current.selectedLanguage
        );
      },
    },
  );

  readonly productPackage$ = this.select(({ selectedCourse }) => selectedCourse?.productPackage);

  readonly selectedCourseAndLanguage$ = this.select(
    ({ selectedCourse, selectedLanguage }) => ({
      selectedCourse,
      selectedLanguage,
    }),
    {
      debounce: true,
      equal: (previous, current) => {
        return previous.selectedCourse?.code === current.selectedCourse?.code && previous.selectedLanguage === current.selectedLanguage;
      },
    },
  );

  readonly selectedCourseStartDateAndEndDate$ = this.select(
    ({ selectedCourse, selectedStartDate, selectedEndDate }) => ({
      selectedCourse,
      selectedStartDate,
      selectedEndDate,
    }),
    {
      debounce: true,
      equal: (previous, current) => {
        return (
          previous.selectedCourse?.code === current.selectedCourse?.code &&
          previous.selectedStartDate === current.selectedStartDate &&
          previous.selectedEndDate === current.selectedEndDate
        );
      },
    },
  );

  readonly ayaCourseDetails$ = this.select(
    ({ selectedLanguage, selectedSchoolLocation, selectedCourse, selectedAyaDuration, selectedStartDate, selectedEndDate, tripDates }) => ({
      selectedLanguage,
      selectedSchoolLocation,
      selectedCourse,
      selectedAyaDuration,
      selectedStartDate,
      selectedEndDate,
      tripDates,
    }),
    { debounce: true },
  );

  readonly courseDetails$ = this.select(
    ({
      selectedLanguage,
      selectedSchoolLocation,
      selectedCourse,
      selectedDuration,
      selectedStartDate,
      selectedEndDate,
      tripDates,
      selectedTimezone,
      selectedClassTime,
    }) => ({
      selectedLanguage,
      selectedSchoolLocation,
      selectedCourse,
      selectedDuration,
      selectedStartDate,
      selectedEndDate,
      tripDates,
      selectedTimezone,
      selectedClassTime,
    }),
    { debounce: true },
  );

  readonly selectedSchoolLocationAndCourse$ = this.select(
    ({ selectedSchoolLocation, selectedCourse }) => ({
      selectedSchoolLocation,
      selectedCourse,
    }),
    {
      debounce: true,
      equal: (prev, curr) => {
        return prev.selectedSchoolLocation?.schoolCode === curr.selectedSchoolLocation?.schoolCode && prev.selectedCourse?.code === curr.selectedCourse?.code;
      },
    },
  );

  readonly selectedSchoolLocationAndCourseAndTimezone$ = this.select(
    ({ selectedSchoolLocation, selectedCourse, selectedTimezone }) => ({
      selectedSchoolLocation,
      selectedCourse,
      selectedTimezone,
    }),
    {
      debounce: true,
      equal: (prev, curr) => {
        return (
          prev.selectedSchoolLocation?.schoolCode === curr.selectedSchoolLocation?.schoolCode &&
          prev.selectedCourse?.code === curr.selectedCourse?.code &&
          prev.selectedTimezone?.timezoneOffset === curr.selectedTimezone?.timezoneOffset
        );
      },
    },
  );

  readonly selectedSchoolLocationCourseAndLanguage$ = this.select(
    ({ selectedSchoolLocation, selectedCourse, selectedLanguage }) => ({
      selectedSchoolLocation,
      selectedCourse,
      selectedLanguage,
    }),
    {
      debounce: true,
      equal: (previous, current) => {
        return (
          previous.selectedSchoolLocation?.schoolCode === current.selectedSchoolLocation?.schoolCode &&
          previous.selectedCourse?.code === current.selectedCourse?.code &&
          previous.selectedLanguage === current.selectedLanguage
        );
      },
    },
  );

  readonly selectedSchoolCodeCourseAndAyaDuration$ = this.select(
    this.selectedSchoolLocation$,
    this.selectedCourse$,
    this.selectedAyaDuration$,
    (selectedSchoolLocation, selectedCourse, selectedAyaDuration) => ({
      selectedSchoolCode: selectedSchoolLocation?.schoolCode,
      selectedCourse,
      selectedAyaDuration,
    }),
    {
      debounce: true,
    },
  );

  readonly selectedSchoolCodeCourseAndDuration$ = this.select(
    this.selectedSchoolLocation$,
    this.selectedCourse$,
    this.selectedDuration$,
    (selectedSchoolLocation, selectedCourse, selectedDuration) => ({
      selectedSchoolCode: selectedSchoolLocation?.schoolCode,
      selectedCourse,
      selectedDuration,
    }),
    {
      debounce: true,
    },
  );

  readonly selectedCourseAndStartDate$ = this.select(
    ({ selectedCourse, selectedStartDate }) => ({
      selectedCourse,
      selectedStartDate,
    }),
    { debounce: true },
  );

  readonly selectedCourseAndDuration$ = this.select(
    ({ selectedCourse, selectedDuration }) => ({
      selectedCourse,
      selectedDuration,
    }),
    { debounce: true },
  );

  readonly selectedDurationAndStartDate$ = this.select(
    ({ selectedStartDate, selectedDuration }) => ({
      selectedStartDate,
      selectedDuration,
    }),
    { debounce: true },
  );

  readonly coursesTree$ = this.select((state) => state.coursesTree);

  readonly coursesTreeAndSelectedCourse$ = this.select(
    this.coursesTree$,
    this.selectedCourse$,
    (coursesTree, selectedCourse) => ({ coursesTree, selectedCourse }),
    {
      debounce: true,
    },
  );

  readonly selectionForAutomaticCourseCleaning = this.select(
    this.coursesTree$,
    this.selectedCourse$,
    this.selectedSchoolLocation$,
    (coursesTree, selectedCourse, selectedSchoolLocation) => ({ coursesTree, selectedCourse, selectedSchoolLocation }),
    {
      debounce: true,
    },
  );
  readonly ayaCourses$ = this.select(({ ayaCourses }) => ayaCourses);

  readonly selectionForAutomaticAyaCourseCleaning$ = this.select(
    this.ayaCourses$,
    this.selectedLanguage$,
    this.selectedCourse$,
    (ayaCourses, selectedLanguage, selectedCourse) => ({ ayaCourses, selectedLanguage, selectedCourse }),
    {
      debounce: true,
    },
  );

  readonly availableDurations$ = this.select(this.selectedCourse$, (selectedCourse) => {
    if (!selectedCourse) {
      return [];
    }

    if (selectedCourse.courseDates?.length > 0) {
      // Fixed course lengths -> get lengths from the courseDates
      return [...new Set(selectedCourse.courseDates.map((courseDate) => courseDate.length).filter(nonNullish))].sort((a, b) => a - b);
    }

    // Dynamic course lengths based on min/max duration
    return [...Array((selectedCourse.durationMax ?? DEFAULT_WEEKS_RANGE.MAX_WEEKS) + 1).keys()].slice(
      Math.max(selectedCourse.durationMin ?? DEFAULT_WEEKS_RANGE.MIN_WEEKS, 1),
    );
  });

  readonly selectionForAutomaticDurationReset$ = this.select(
    this.availableDurations$,
    this.selectedDuration$,
    (availableDurations, selectedDuration) => ({ availableDurations, selectedDuration }),
    {
      debounce: true,
    },
  );

  // Updaters
  readonly updateState = this.updater((state, partialState: Partial<CourseState>) => ({
    ...state,
    ...partialState,
  }));

  // Effects

  /**
   * Updates the selected start date and end date based on the selected start date. To do that,
   * there is a call to the tripDatesService to get the tuition dates.
   */
  readonly updateSelectedStartDate$ = this.effect((selectedStartDate$: Observable<string>) => {
    return selectedStartDate$.pipe(
      switchMap((selectedStartDate) => combineLatest([of(selectedStartDate), this.selectedSchoolCodeCourseAndDuration$, this.quoteToolLiteStore.activeFlow$])),
      switchMap(([selectedStartDate, { selectedSchoolCode, selectedDuration, selectedCourse }, activeFlow]) =>
        iif(
          () => this.validSelection(selectedCourse, selectedDuration, selectedStartDate),
          this.tripDatesService.getTripDates(
            selectedSchoolCode,
            selectedCourse?.code,
            selectedDuration,
            selectedStartDate,
            activeFlow === Flows.Aya ? DurationTypes.Months : DurationTypes.Weeks,
          ),
          of({
            tuitionStartDate: undefined,
            tuitionEndDate: undefined,
            accommodationStartDate: undefined,
            accommodationEndDate: undefined,
          } as TripDates),
        ),
      ),
      tapResponse(
        (tripDates: TripDates) => {
          this.setState((state) => ({
            ...state,
            selectedStartDate: tripDates?.tuitionStartDate,
            selectedEndDate: tripDates?.tuitionEndDate,
            tripDates: { ...tripDates },
          }));
        },
        () => this.setState((state) => ({ ...state })),
      ),
    );
  });

  readonly updateSelectedAyaStartDate$ = this.effect((selectedStartDate$: Observable<string>) => {
    return selectedStartDate$.pipe(
      switchMap((selectedStartDate) =>
        combineLatest([of(selectedStartDate), this.selectedSchoolCodeCourseAndAyaDuration$, this.quoteToolLiteStore.activeFlow$]),
      ),
      switchMap(([selectedStartDate, { selectedSchoolCode, selectedAyaDuration, selectedCourse }, activeFlow]) =>
        iif(
          () => this.validSelection(selectedCourse, selectedAyaDuration?.months, selectedStartDate),
          this.tripDatesService.getTripDates(
            selectedSchoolCode,
            selectedCourse?.code,
            selectedAyaDuration?.months,
            selectedStartDate,
            activeFlow === Flows.Aya ? DurationTypes.Months : DurationTypes.Weeks,
          ),
          of({
            tuitionStartDate: undefined,
            tuitionEndDate: undefined,
            accommodationStartDate: undefined,
            accommodationEndDate: undefined,
          } as TripDates),
        ),
      ),
      tapResponse(
        (tripDates: TripDates) => {
          this.setState((state) => ({
            ...state,
            selectedStartDate: tripDates?.tuitionStartDate,
            selectedEndDate: tripDates?.tuitionEndDate,
            tripDates: { ...tripDates },
          }));
        },
        () => this.setState((state) => ({ ...state })),
      ),
    );
  });

  /**
   * It prepares the observable that fills the aya courses when the selected language changes.
   * It subscribes to changes in the selected language, and when it changes, it calls the aya course service to get the courses for the selected language.
   * The aya courses is then updated in the state.
   * This is only for Aya flow.
   */
  private prepareAyaCoursesFillingOnLanguageChange() {
    this.quoteToolLiteStore.activeFlow$
      .pipe(
        filter((flow) => flow === Flows.Aya),
        switchMap(() => this.selectedLanguage$),
        switchMap((selectedLanguage: string) => this.ayaCourseService.getGroupedCourses(selectedLanguage)),
        takeUntilDestroyed(),
      )
      .subscribe((ayaCourses: GroupedAyaCourse[]) => {
        this.updateState({ ayaCourses });
      });
  }

  /**
   * It prepares the observable that clears the selected course if it is not in the list of available courses.
   * It subscribes to changes in the courses and the selected language and course, and if the selected course is not found in the list,
   * it updates the state to set the selected course to undefined.
   * This is only for Aya flow.
   */
  private prepareAyaSelectedCourseResetOnUnavailable() {
    this.quoteToolLiteStore.activeFlow$
      .pipe(
        filter((flow) => flow === Flows.Aya),
        switchMap(() => this.selectionForAutomaticAyaCourseCleaning$),
        filter(({ ayaCourses, selectedLanguage, selectedCourse }) => nonNullish(ayaCourses) && nonNullish(selectedLanguage) && nonNullish(selectedCourse)),
        takeUntilDestroyed(),
      )
      .subscribe(({ ayaCourses, selectedLanguage, selectedCourse }) => {
        // if there are no courses, the aya courses have not loaded yet, do not clear the selected course
        if (!ayaCourses?.length) {
          return;
        }

        let foundCourseCode: string | undefined;
        for (const course of ayaCourses) {
          const courseItem = course.items.find(({ value: courseCode }: SelectItem) => courseCode === selectedCourse?.code);
          if (courseItem) {
            foundCourseCode = courseItem?.value;
            break;
          }
        }

        // If the course is not found, or there simply is no selected school -> clear the selected course in the store
        if (!foundCourseCode || !selectedLanguage) {
          this.updateState({ selectedCourse: undefined });
        }
      });
  }

  private validSelection(selectedCourse: CourseDetails, selectedDuration: number, selectedStartDate: string): boolean {
    if (selectedCourse?.courseDates?.length > 0) {
      // if the course has fixed dates, check if the selected start date and duration are valid
      return selectedCourse.courseDates.some((courseDate) => courseDate.from === selectedStartDate && courseDate.length === selectedDuration);
    }

    return true;
  }

  /**
   * It prepares the observable that fills the courses tree when the school location changes.
   * It subscribes to changes in the selected school location, and when it changes, it calls the course service to get the courses tree for the selected school location.
   * The courses tree is then updated in the state.
   * This is only for Adult flow.
   */
  private prepareAdultCoursesTreeFillingOnSchoolLocationChange() {
    this.quoteToolLiteStore.activeFlow$
      .pipe(
        filter((flow) => flow === Flows.Adult),
        switchMap(() => this.selectedSchoolLocation$),
        switchMap((selectedSchoolLocation: SchoolLocation) => this.courseService.getCoursesTree(selectedSchoolLocation?.schoolCode)),
        takeUntilDestroyed(),
      )
      .subscribe((treeCourses: CourseTreeNode[]) => {
        this.updateState({ coursesTree: treeCourses });
      });
  }

  /**
   * It prepares the observable tha clears the selected course if it is not in the list of available courses.
   * It subscribes to changes in the courses tree and the selected course, and if the selected course is not found in the tree,
   * it updates the state to set the selected course to undefined.
   * This is only for Adult flow.
   */
  private prepareAdultSelectedCourseResetOnUnavailable() {
    this.quoteToolLiteStore.activeFlow$
      .pipe(
        filter((flow) => flow === Flows.Adult),
        switchMap(() => this.selectionForAutomaticCourseCleaning),
        filter(({ coursesTree, selectedCourse }) => nonNullish(coursesTree) && nonNullish(selectedCourse)),
        takeUntilDestroyed(),
      )
      .subscribe(({ coursesTree, selectedCourse, selectedSchoolLocation }) => {
        // if there is selected school but not tree courses, the tree courses have not loaded yet, do not clear the selected course
        if (selectedSchoolLocation && !coursesTree?.length) {
          return;
        }

        // Get all children courses
        const childrenCourses = coursesTree?.flatMap((course) => course.children);

        // Find the course in the list of available courses that matches the selected course in the store
        const course = childrenCourses?.find((course) => {
          return course.key === selectedCourse?.code;
        });

        // If the course is not found, or there simply is no selected school -> clear the selected course in the store
        if (!course || !selectedSchoolLocation) {
          this.updateState({ selectedCourse: undefined });
        }
      });
  }

  /**
   * It resets the selected dates when the product package changes.
   */
  private resetSelectedDatesWhenPackageChanges() {
    this.productPackage$.pipe(skip(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.updateState({
        selectedDuration: undefined,
        selectedStartDate: undefined,
        selectedEndDate: undefined,
        tripDates: undefined,
      });
    });
  }

  /**
   * It prepares the observable that resets the selected duration when it becomes incompatible with available durations.
   * It subscribes to changes in available durations and the selected duration, and if the selected duration is not
   * in the list of available durations, it updates the state to set the selected duration to undefined.
   */
  private prepareSelectedDurationResetOnUnavailable() {
    this.selectionForAutomaticDurationReset$
      .pipe(
        filter(({ selectedDuration }) => nonNullish(selectedDuration)),
        takeUntilDestroyed(),
      )
      .subscribe(({ availableDurations, selectedDuration }) => {
        // Check if the selected duration is in the list of available durations
        const validDuration = availableDurations?.some((duration) => duration === selectedDuration);

        // If the selected duration is not valid, clear it and dependent date state from the store
        if (!validDuration) {
          this.updateState({
            selectedDuration: undefined,
            selectedStartDate: undefined,
            selectedEndDate: undefined,
            tripDates: undefined,
          });
        }
      });
  }
}
